/**
 * Bộ chọn phạm vi địa bàn — thành phần dùng chung của mục 3.6 đặc tả giao diện.
 *
 * Xuất hiện ở thanh ngữ cảnh, trong biểu mẫu lịch phát, trong phát khẩn cấp và
 * trong phân quyền người dùng, nên hiện thực MỘT LẦN ở đây; các đợt sau dùng lại,
 * không sao chép mã.
 *
 * Bốn yêu cầu bắt buộc của mục 3.6, tất cả đều được hiện thực:
 *  1. cây ba cấp tỉnh / xã, phường, đặc khu / thôn, tổ dân phố;
 *  2. chọn nhiều nút, có trạng thái CHỌN MỘT PHẦN (dấu trừ) cho nút cha;
 *  3. chọn nút cha nghĩa là chọn toàn bộ nút con TẠI THỜI ĐIỂM THỰC HIỆN LỆNH —
 *     giao diện nói rõ điều này bằng chữ, không để người dùng tự đoán;
 *  4. số cụm loa cạnh mỗi nút để ước lượng phạm vi ảnh hưởng trước khi xác nhận.
 *
 * Người dùng cấp xã chỉ thấy nhánh được phân công: máy chủ CẮT cây trước khi trả
 * về, nên các nhánh khác không tải về trình duyệt.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { thietBiApi, type NutDiaBan } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from './TrangThaiManHinh';

export interface BoChonPhamViDiaBanProps {
  /** Mã địa bàn đang chọn. */
  giaTri: string[];
  onXacNhan: (ds: string[]) => void;
  onHuy: () => void;
  /** Cây nạp sẵn, dùng cho kiểm thử thành phần. */
  cayNapSan?: NutDiaBan[];
}

interface Nut extends NutDiaBan {
  con: Nut[];
}

/** Dựng cây ba cấp từ danh sách phẳng theo `maCha`. */
export function dungCay(ds: NutDiaBan[]): Nut[] {
  const bang = new Map<string, Nut>();
  for (const n of ds) bang.set(n.maDinhDanh, { ...n, con: [] });
  const goc: Nut[] = [];
  for (const n of bang.values()) {
    const cha = n.maCha ? bang.get(n.maCha) : undefined;
    if (cha) cha.con.push(n);
    else goc.push(n);
  }
  return goc;
}

export type TrangThaiChon = 'khong' | 'mot-phan' | 'day-du';

/**
 * Trạng thái chọn của một nút: đầy đủ khi chính nó HOẶC một nút cha được chọn,
 * một phần khi có nút con nào đó được chọn.
 *
 * `chaDaChon` là điều kiện bắt buộc của mục 3.6: chọn một nút cấp trên nghĩa là
 * chọn toàn bộ nút con của nó, nên nút con phải HIỆN RA là đã chọn. Không truyền
 * cờ này thì người dùng chọn cả tỉnh vẫn thấy mọi xã bỏ trống và tưởng mình chưa
 * chọn gì.
 */
export function trangThaiChonCua(nut: Nut, daChon: Set<string>, chaDaChon = false): TrangThaiChon {
  if (chaDaChon || daChon.has(nut.maDinhDanh)) return 'day-du';
  if (nut.con.length === 0) return 'khong';
  const cac = nut.con.map((c) => trangThaiChonCua(c, daChon, false));
  if (cac.every((x) => x === 'day-du')) return 'day-du';
  if (cac.some((x) => x !== 'khong')) return 'mot-phan';
  return 'khong';
}

/** Đếm số xã, thôn, cụm loa nằm trong tập đã chọn. */
export function demDaChon(goc: Nut[], daChon: Set<string>): { xa: number; thon: number; cumLoa: number } {
  let xa = 0;
  let thon = 0;
  let cumLoa = 0;
  const di = (n: Nut, chaDaChon: boolean): void => {
    const tuChon = chaDaChon || daChon.has(n.maDinhDanh);
    if (tuChon) {
      if (n.cap === 'xa') xa += 1;
      if (n.cap === 'thon') thon += 1;
      // Cụm loa chỉ tính một lần ở nút được chọn cao nhất để không đếm trùng.
      if (!chaDaChon) cumLoa += n.soThietBi;
    }
    for (const c of n.con) di(c, tuChon);
  };
  for (const n of goc) di(n, false);
  return { xa, thon, cumLoa };
}

export function BoChonPhamViDiaBan({
  giaTri,
  onXacNhan,
  onHuy,
  cayNapSan,
}: BoChonPhamViDiaBanProps): ReactNode {
  const [daChon, datDaChon] = useState<Set<string>>(new Set(giaTri));
  const [tuKhoa, datTuKhoa] = useState('');

  const kq = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => (cayNapSan ? Promise.resolve(cayNapSan) : thietBiApi.cayDiaBan({}, signal)),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [cayNapSan],
  });

  const cay = useMemo(() => dungCay(kq.duLieu ?? []), [kq.duLieu]);
  const dem = useMemo(() => demDaChon(cay, daChon), [cay, daChon]);

  const doi = (ma: string): void => {
    datDaChon((cu) => {
      const moi = new Set(cu);
      if (moi.has(ma)) moi.delete(ma);
      else moi.add(ma);
      return moi;
    });
  };

  const veNut = (n: Nut, capSau: number, chaDaChon = false): ReactNode => {
    const tt = trangThaiChonCua(n, daChon, chaDaChon);
    const khop =
      tuKhoa.trim().length < 2 ||
      n.ten.toLowerCase().includes(tuKhoa.trim().toLowerCase()) ||
      n.maDinhDanh.toLowerCase().includes(tuKhoa.trim().toLowerCase());
    const conVe = n.con.map((c) => veNut(c, capSau + 1, tt === 'day-du'));
    const coConHien = conVe.some((x) => x !== null);
    if (!khop && !coConHien) return null;
    return (
      <li key={n.maDinhDanh} style={{ paddingLeft: `${capSau * 16}px` }}>
        <label className="pham-vi__nut">
          <input
            type="checkbox"
            checked={tt === 'day-du'}
            ref={(e) => {
              if (e) e.indeterminate = tt === 'mot-phan';
            }}
            disabled={chaDaChon}
            onChange={() => doi(n.maDinhDanh)}
            aria-describedby={tt === 'mot-phan' ? 'pham-vi-mot-phan' : undefined}
          />
          {/* Dấu trừ là trạng thái chọn một phần; kèm chữ để không chỉ dựa vào hình. */}
          <span className="pham-vi__danh-dau" aria-hidden="true">
            {tt === 'day-du' ? '☑' : tt === 'mot-phan' ? '⊟' : '☐'}
          </span>
          <span className="pham-vi__ten">{n.ten}</span>
          <span className="pham-vi__so">
            ({n.soThietBi} {vi.phamVi.cumLoa})
          </span>
          {tt === 'mot-phan' ? <span className="pham-vi__mot-phan">{vi.phamVi.chonMotPhan}</span> : null}
        </label>
        {coConHien ? <ul>{conVe}</ul> : null}
      </li>
    );
  };

  return (
    <div className="man-che" role="presentation">
      <div className="hop-thoai pham-vi" role="dialog" aria-modal="true" aria-label={vi.phamVi.tieuDe}>
        <h2>{vi.phamVi.tieuDe}</h2>
        <label htmlFor="pham-vi-tim">{vi.phamVi.tim}</label>
        <input id="pham-vi-tim" type="search" value={tuKhoa} onChange={(e) => datTuKhoa(e.target.value)} />

        {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={5} soCot={1} taiLau={kq.taiLau} /> : null}
        {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
        {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.phamVi.loiCay} /> : null}
        {kq.trangThai === 'co-du-lieu' ? (
          <ul className="pham-vi__cay">{cay.map((n) => veNut(n, 0))}</ul>
        ) : null}

        <p id="pham-vi-mot-phan" className="pham-vi__ghi-chu">
          {vi.phamVi.ghiChuNutCha}
        </p>
        <p className="pham-vi__ghi-chu">{vi.phamVi.ghiChuPhamViNguoiDung}</p>
        <p className="pham-vi__tong" data-vung="pham-vi-tong">
          {vi.phamVi.daChon}: {dem.xa} {vi.phamVi.xa}, {dem.thon} {vi.phamVi.thon}, {dem.cumLoa}{' '}
          {vi.phamVi.cumLoa}
        </p>
        <div className="hop-thoai__nut">
          <button type="button" className="nut" onClick={onHuy}>
            {vi.phamVi.huy}
          </button>
          <button type="button" className="nut nut--chinh" onClick={() => onXacNhan([...daChon])}>
            {vi.phamVi.xacNhan}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Nhãn phạm vi hiển thị ở thanh ngữ cảnh. `tenTheoMa` (từ `tenPhamVi` của hồ sơ)
 * cho phép hiện tên thay vì mã khi chỉ có một địa bàn.
 */
export function nhanPhamVi(ds: string[], tenTheoMa?: ReadonlyMap<string, string>): string {
  if (ds.length === 0) return vi.nguCanh.toanTinh;
  if (ds.length === 1) return tenTheoMa?.get(ds[0] as string) ?? (ds[0] as string);
  return dienVao('{so} địa bàn', { so: ds.length });
}
